/**
 * Volet « thème » des Réglages (specs §6.8 : « Thème : clair / sombre /
 * système », et §8.1 : « thème sombre et clair de qualité égale »).
 *
 * Dernière ligne du §6.8 qui restait à livrer — l'étape 5 avait posé le
 * planning, l'étape 8 le renvoi vers l'objectif de poids, l'étape 9 les
 * notifications.
 *
 * ⚠️ **Aucune requête, aucune mutation.** Le choix est conservé sur l'appareil
 * et non en base : le raisonnement complet, et l'écart au §4.7 qu'il constitue,
 * sont en tête de `lib/theme.ts`. En deux mots : le thème doit être appliqué
 * avant le premier pixel, il doit fonctionner sur l'écran de connexion où il n'y
 * a pas encore de session, et « système » est par nature propre à l'appareil.
 */

import { useEffect, useState } from 'react';

import { Button } from '../../components/nourri';
import { Hint } from '../../components/ui/primitives';
import {
  applyTheme,
  storeChoice,
  storedChoice,
  watchSystemTheme,
  type ThemeChoice,
} from '../../lib/theme';

const OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
  { value: 'system', label: 'Système' },
];

export function ThemeSection() {
  const [choice, setChoice] = useState<ThemeChoice>(storedChoice);

  /*
   * Suit le réglage du système — mais **seulement** quand le choix est
   * « système ». Sans cette garde, basculer l'iPhone en sombre écraserait un
   * choix « clair » explicite.
   *
   * ⚠️ L'écouteur est retiré au démontage : `matchMedia` renvoie le même
   * `MediaQueryList` à chaque appel, donc sans retrait chaque passage dans les
   * Réglages en empilerait un de plus sur le même objet.
   */
  useEffect(() => {
    if (choice !== 'system') return;
    return watchSystemTheme(() => {
      applyTheme('system');
    });
  }, [choice]);

  const pick = (value: ThemeChoice) => {
    setChoice(value);
    storeChoice(value);
    // 🔴 C'est ici que `<meta name="theme-color">` est réécrite — la barre
    // d'état d'une PWA installée suit donc la bascule, et pas seulement le
    // chargement. Cf. `applyTheme`.
    applyTheme(value);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <h2 className="n-title">Thème</h2>

      <div role="radiogroup" aria-label="Thème" className="n-segmented">
        {OPTIONS.map((option) => {
          const active = choice === option.value;
          return (
            <Button
              key={option.value}
              role="radio"
              aria-checked={active}
              variant={active ? 'selected' : 'secondary'}
              shape="rounded"
              onClick={() => {
                pick(option.value);
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </div>

      <Hint>
        « Système » suit le réglage de cet appareil. Le choix vaut pour cet appareil
        seulement — l’iPhone et l’ordinateur peuvent donc différer, ce qui est voulu.
      </Hint>
    </div>
  );
}
